import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { createPortal } from "react-dom";

import {
    dinoAssistantStateAssets,
    dinoAssistantUiIconAssets
} from "../../assets/dino-assistant/dinoAssistantAssets.js";
import type {
    DinoAssistantState
} from "../../assets/dino-assistant/dinoAssistantAssets.js";
import { useI18n } from "../../i18n/I18nProvider.js";
import { useSpeech } from "../../services/speech/index.js";
import {
    canonicalTutorEngine,
    extractTutorSource,
    guardedHintEngine,
    type HintEngine,
    type TutorEngine,
    type TutorExplanation,
    type TutorHint
} from "../../services/tutor/index.js";
import {
    pickSpeechTextSelection,
    pickSpeechWordAtPoint
} from "../../services/speech/speechWordPicker.js";
import { useDinoContext } from "./context/index.js";
import {
    selectDinoAssistantActions,
    type DinoAssistantFutureAction
} from "./dinoAssistantRecommendations.js";
import {
    DINO_ASSISTANT_OPEN_EVENT,
    type DinoAssistantOpenDetail
} from "./dinoAssistantEvents.js";

interface DinoAssistantProps {
    showLauncher?: boolean;
    renderInline?: boolean;
    defaultOpen?: boolean;
    /** Human-readable page/topic supplied by the typed context bus. */
    contextLabel?: string;
    /** Replaceable boundary for a future local or remote tutor engine. */
    tutorEngine?: TutorEngine;
    /** Safe hint policy kept separate from answer validation. */
    hintEngine?: HintEngine;
}

interface AssistantCopy {
    name: string;
    tagline: string;
    open: string;
    close: string;
    available: string;
    comingSoon: string;
    readContext: string;
    readContextDescription: string;
    explainContext: string;
    explainContextDescription: string;
    explaining: string;
    explanationError: string;
    closeExplanation: string;
    canonicalSource: string;
    hintContext: string;
    hintContextDescription: string;
    hinting: string;
    hintError: string;
    closeHint: string;
    guidedHintSource: string;
    readWord: string;
    readWordDescription: string;
    pickWordHint: string;
    readSelection: string;
    exitWordMode: string;
    settingsLabel: string;
    stop: string;
    stopDescription: string;
    pause: string;
    pauseDescription: string;
    resume: string;
    resumeDescription: string;
    speed: string;
    voice: string;
    automaticVoice: string;
    naturalVoice: string;
    readingNow: string;
    unsupported: string;
    futureTitle: string;
    futureDescription: string;
    privacy: string;
    actions: Record<DinoAssistantFutureAction, string>;
    descriptions: Record<DinoAssistantFutureAction, string>;
}

const copyByLanguage: Record<"fr" | "fa", AssistantCopy> = {
    fr: {
        name: "Dino, ton assistant",
        tagline: "Je t’accompagne dans ta lecture et te guide dans les exercices.",
        open: "Ouvrir Dino, l’assistant pédagogique",
        close: "Fermer l’assistant Dino",
        available: "Disponible maintenant",
        comingSoon: "À venir",
        readContext: "Lire cette carte",
        readContextDescription: "Écouter uniquement le contenu que tu viens de montrer à Dino.",
        explainContext: "M’expliquer",
        explainContextDescription: "Revoir l’essentiel à partir du contenu exact de cette carte.",
        explaining: "Dino prépare l’explication…",
        explanationError: "Cette carte ne contient pas encore assez de contenu à expliquer.",
        closeExplanation: "Fermer l’explication",
        canonicalSource: "Contenu de la leçon",
        hintContext: "Donner un indice",
        hintContextDescription: "Avancer avec une stratégie sans voir la réponse attendue.",
        hinting: "Dino prépare un indice…",
        hintError: "Dino ne peut pas encore guider cette question sans risquer de révéler la réponse.",
        closeHint: "Fermer l’indice",
        guidedHintSource: "Indice guidé",
        readWord: "Lire un mot",
        readWordDescription: "Choisir directement un mot dans la page.",
        pickWordHint: "Clique un mot ou sélectionne une expression.",
        readSelection: "Relire la sélection",
        exitWordMode: "Terminer",
        settingsLabel: "Réglages de lecture",
        stop: "Arrêter",
        stopDescription: "Interrompre la lecture en cours.",
        pause: "Pause",
        pauseDescription: "Mettre la lecture en pause.",
        resume: "Reprendre",
        resumeDescription: "Continuer la lecture.",
        speed: "Vitesse",
        voice: "Voix",
        automaticVoice: "Meilleure voix disponible",
        naturalVoice: "Naturelle",
        readingNow: "Mot lu",
        unsupported: "La lecture audio n’est pas disponible dans ce navigateur.",
        futureTitle: "Bientôt avec Dino",
        futureDescription: "Les prochaines aides ont déjà leur place, sans promettre une fonction incomplète.",
        privacy: "Le microphone et la caméra demanderont toujours ton accord avant de démarrer.",
        actions: {
            explain: "M’expliquer",
            hint: "Donner un indice",
            whyWrong: "Pourquoi c’est faux ?",
            simpler: "Dire plus simplement",
            translate: "Traduire",
            quiz: "Me faire réviser",
            microphone: "Parler avec Dino",
            camera: "Montrer à Dino"
        },
        descriptions: {
            explain: "Comprendre un mot, une phrase ou une règle.",
            hint: "Avancer sans révéler directement la réponse.",
            whyWrong: "Recevoir une explication liée à ta réponse.",
            simpler: "Reformuler avec un vocabulaire plus accessible.",
            translate: "Passer du français au persan, et inversement.",
            quiz: "Créer des questions depuis le contenu affiché.",
            microphone: "T’entraîner à parler et à prononcer.",
            camera: "Utiliser une image dans une activité guidée."
        }
    },
    fa: {
        name: "دینو، دستیار تو",
        tagline: "در خواندن همراهت هستم و در تمرین‌ها مرحله‌به‌مرحله راهنمایی‌ات می‌کنم.",
        open: "باز کردن دینو، دستیار آموزشی",
        close: "بستن دستیار دینو",
        available: "همین حالا در دسترس",
        comingSoon: "به‌زودی",
        readContext: "خواندن این بخش",
        readContextDescription: "فقط محتوایی را که به دینو نشان دادی گوش کن.",
        explainContext: "برایم توضیح بده",
        explainContextDescription: "نکته‌های اصلی را فقط از محتوای همین بخش مرور کن.",
        explaining: "دینو در حال آماده‌کردن توضیح است…",
        explanationError: "این بخش هنوز محتوای کافی برای توضیح ندارد.",
        closeExplanation: "بستن توضیح",
        canonicalSource: "محتوای درس",
        hintContext: "راهنمایی بده",
        hintContextDescription: "با یک راهبرد جلو برو، بدون دیدن پاسخ مورد انتظار.",
        hinting: "دینو در حال آماده‌کردن راهنمایی است…",
        hintError: "دینو هنوز نمی‌تواند بدون خطر آشکارکردن پاسخ برای این سؤال راهنمایی بدهد.",
        closeHint: "بستن راهنمایی",
        guidedHintSource: "راهنمایی مرحله‌ای",
        readWord: "خواندن یک واژه",
        readWordDescription: "یک واژه را مستقیماً در صفحه انتخاب کن.",
        pickWordHint: "روی یک واژه بزن یا یک عبارت را انتخاب کن.",
        readSelection: "خواندن دوبارهٔ انتخاب",
        exitWordMode: "پایان",
        settingsLabel: "تنظیمات خواندن",
        stop: "توقف",
        stopDescription: "خواندن در حال اجرا را متوقف کن.",
        pause: "مکث",
        pauseDescription: "خواندن را موقتاً متوقف کن.",
        resume: "ادامه",
        resumeDescription: "خواندن را ادامه بده.",
        speed: "سرعت",
        voice: "صدا",
        automaticVoice: "بهترین صدای موجود",
        naturalVoice: "طبیعی",
        readingNow: "واژهٔ در حال خواندن",
        unsupported: "خواندن صوتی در این مرورگر در دسترس نیست.",
        futureTitle: "به‌زودی با دینو",
        futureDescription: "قابلیت‌های بعدی از حالا جای مشخصی دارند، بدون وعدهٔ ناقص.",
        privacy: "میکروفون و دوربین همیشه پیش از شروع از تو اجازه می‌گیرند.",
        actions: {
            explain: "برایم توضیح بده",
            hint: "راهنمایی بده",
            whyWrong: "چرا اشتباه است؟",
            simpler: "ساده‌تر بگو",
            translate: "ترجمه کن",
            quiz: "از من سؤال بپرس",
            microphone: "با دینو صحبت کن",
            camera: "به دینو نشان بده"
        },
        descriptions: {
            explain: "یک واژه، جمله یا قاعده را بهتر بفهم.",
            hint: "بدون دیدن مستقیم پاسخ، جلو برو.",
            whyWrong: "برای پاسخ خودت توضیح دریافت کن.",
            simpler: "مطلب را با واژه‌های ساده‌تر بخوان.",
            translate: "میان فارسی و فرانسوی ترجمه کن.",
            quiz: "از محتوای صفحه چند پرسش بساز.",
            microphone: "گفتار و تلفظ را تمرین کن.",
            camera: "از یک تصویر در فعالیت هدایت‌شده استفاده کن."
        }
    }
};

const futureActions: ReadonlyArray<{
    id: DinoAssistantFutureAction;
    iconAsset: string;
}> = [
    { id: "whyWrong", iconAsset: dinoAssistantUiIconAssets.wrong },
    { id: "simpler", iconAsset: dinoAssistantUiIconAssets.simpler },
    { id: "translate", iconAsset: dinoAssistantUiIconAssets.translate },
    { id: "quiz", iconAsset: dinoAssistantUiIconAssets.quiz },
    { id: "microphone", iconAsset: dinoAssistantUiIconAssets.microphone },
    { id: "camera", iconAsset: dinoAssistantUiIconAssets.camera }
];

interface ActiveDinoSurface {
    contextLabel?: string;
    readingTarget: HTMLElement;
    releaseContext?: () => void;
}

type TutorResponse = TutorExplanation | TutorHint;

interface ActiveTutorResponse {
    readonly target: HTMLElement;
    readonly response: TutorResponse;
}

function DinoAssistant({
    showLauncher = true,
    renderInline = false,
    defaultOpen = false,
    contextLabel,
    tutorEngine = canonicalTutorEngine,
    hintEngine = guardedHintEngine
}: DinoAssistantProps) {
    const { language } = useI18n();
    const copy = copyByLanguage[language];
    const speech = useSpeech();
    const tutorContext = useDinoContext();
    const stopSpeech = speech.stop;
    const [open, setOpen] = useState(defaultOpen);
    const [wordMode, setWordMode] = useState(false);
    const [selectedText, setSelectedText] = useState<string | null>(null);
    const selectedRangeRef = useRef<Range | null>(null);
    const [previewState, setPreviewState] =
        useState<DinoAssistantState>("idle");
    const [activeSurface, setActiveSurface] =
        useState<ActiveDinoSurface | null>(null);
    const [tutorResponse, setTutorResponse] =
        useState<ActiveTutorResponse | null>(null);
    const [tutorStatus, setTutorStatus] =
        useState<"idle" | "explaining" | "hinting" | "explanation-error" | "hint-error">("idle");
    const activeSurfaceRef = useRef<ActiveDinoSurface | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const previousSpeechStatus = useRef(speech.status);
    const [isMobileSheet, setIsMobileSheet] = useState(false);

    const recommendedActionIds = useMemo(
        () => selectDinoAssistantActions(tutorContext),
        [tutorContext]
    );
    const recommendedActions = useMemo(
        () => recommendedActionIds.flatMap(id => {
            const action = futureActions.find(candidate => candidate.id === id);

            return action ? [action] : [];
        }),
        [recommendedActionIds]
    );
    const canGiveHint = Boolean(
        activeSurface
        && tutorContext.exercise?.phase === "active"
        && tutorContext.exercise.type
        && tutorContext.exercise.objective
        && tutorContext.exercise.prompt
    );
    const canExplainContext = Boolean(
        activeSurface
        && tutorContext.page?.kind === "grammar"
        && tutorContext.lesson?.id
        && !tutorContext.exercise
    );
    const tutorBusy = tutorStatus === "explaining" || tutorStatus === "hinting";
    const relevantVoices = useMemo(() => {
        const languagePrefix = language === "fa" ? "fa" : "fr";

        return speech.voices
            .filter(voice => voice.lang.toLowerCase().startsWith(languagePrefix))
            .sort((left, right) => {
                if (left.quality !== right.quality) {
                    return left.quality === "natural" ? -1 : 1;
                }

                return left.name.localeCompare(right.name);
            });
    }, [language, speech.voices]);
    const selectedVoiceId = relevantVoices.some(
        voice => voice.id === speech.settings.voiceId
    )
        ? speech.settings.voiceId ?? ""
        : "";

    const visualState = useMemo<DinoAssistantState>(
        () => {
            if (speech.status === "speaking") {
                return "speaking-2";
            }

            if (speech.status === "paused") {
                return "reading";
            }

            if (speech.status === "error") {
                return "oops";
            }

            return previewState;
        },
        [previewState, speech.status]
    );

    const releaseActiveSurface = useCallback((): void => {
        activeSurfaceRef.current?.releaseContext?.();
        activeSurfaceRef.current = null;
        setActiveSurface(null);
    }, []);

    const close = useCallback((): void => {
        stopSpeech();
        releaseActiveSurface();
        setWordMode(false);
        setOpen(false);
        setPreviewState("idle");
    }, [releaseActiveSurface, stopSpeech]);

    const show = useCallback((detail: DinoAssistantOpenDetail = {}): void => {
        setWordMode(false);
        releaseActiveSurface();
        setTutorStatus("idle");

        if (
            detail.readingTarget
            && tutorResponse?.target !== detail.readingTarget
        ) {
            setTutorResponse(null);
        }

        if (detail.readingTarget) {
            const nextSurface: ActiveDinoSurface = {
                readingTarget: detail.readingTarget,
                contextLabel: detail.contextLabel,
                releaseContext: detail.releaseContext
            };

            activeSurfaceRef.current = nextSurface;
            setActiveSurface(nextSurface);
        }

        if (typeof document !== "undefined") {
            returnFocusRef.current =
                document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null;
        }

        setOpen(true);
    }, [releaseActiveSurface, tutorResponse?.target]);

    useEffect(() => {
        const handleOpen = (event: Event): void => {
            show((event as CustomEvent<DinoAssistantOpenDetail>).detail);
        };

        window.addEventListener(DINO_ASSISTANT_OPEN_EVENT, handleOpen);

        return () => {
            window.removeEventListener(DINO_ASSISTANT_OPEN_EVENT, handleOpen);
        };
    }, [show]);

    useEffect(
        () => () => {
            activeSurfaceRef.current?.releaseContext?.();
        },
        []
    );

    useEffect(() => {
        const media = window.matchMedia("(max-width: 35rem)");
        const synchronize = (): void => setIsMobileSheet(media.matches);

        synchronize();
        media.addEventListener("change", synchronize);

        return () => media.removeEventListener("change", synchronize);
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        const previousOverflow = document.body.style.overflow;

        if (isMobileSheet) {
            document.body.style.overflow = "hidden";
        }
        const focusFrame = requestAnimationFrame(
            () => closeButtonRef.current?.focus()
        );
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                close();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            cancelAnimationFrame(focusFrame);
            window.removeEventListener("keydown", handleKeyDown);
            if (isMobileSheet) {
                document.body.style.overflow = previousOverflow;
            }
            returnFocusRef.current?.focus();
        };
    }, [close, isMobileSheet, open]);

    useEffect(() => {
        if (!wordMode) return;

        document.documentElement.classList.add("dino-word-reading-mode");
        const synchronizeSelection = (): void => {
            const selection = pickSpeechTextSelection(document);
            selectedRangeRef.current = selection?.range ?? null;
            setSelectedText(selection?.word ?? null);
        };
        const handleWordRelease = (event: PointerEvent): void => {
            if (event.button !== 0) return;
            const target = event.target;
            if (
                target instanceof Element
                && target.closest(".dino-word-reader-toolbar, .dino-assistant-panel")
            ) {
                return;
            }

            const textSelection = pickSpeechTextSelection(document);
            if (textSelection) {
                selectedRangeRef.current = textSelection.range;
                setSelectedText(textSelection.word);
                void speech.speakSelection(
                    textSelection.word,
                    textSelection.range
                );
                return;
            }

            const wordSelection = pickSpeechWordAtPoint(
                document,
                event.clientX,
                event.clientY
            );
            if (!wordSelection) return;

            event.preventDefault();
            event.stopPropagation();
            setSelectedText(null);
            selectedRangeRef.current = null;
            void speech.speakSelection(
                wordSelection.word,
                wordSelection.range
            );
        };

        document.addEventListener("pointerup", handleWordRelease, true);
        document.addEventListener("selectionchange", synchronizeSelection);

        return () => {
            document.documentElement.classList.remove("dino-word-reading-mode");
            document.removeEventListener("pointerup", handleWordRelease, true);
            document.removeEventListener("selectionchange", synchronizeSelection);
        };
    }, [speech, wordMode]);

    useEffect(() => {
        const wasActive =
            previousSpeechStatus.current === "speaking"
            || previousSpeechStatus.current === "paused";

        if (speech.status === "idle" && wasActive) {
            setPreviewState("idle");
        }

        previousSpeechStatus.current = speech.status;
    }, [speech.status]);

    async function handleReadContext(): Promise<void> {
        const readingTarget = activeSurface?.readingTarget;
        if (!readingTarget?.isConnected) return;

        setPreviewState("reading");
        try {
            await speech.speakElement(readingTarget);
        } catch {
            setPreviewState("oops");
        }
    }

    async function handleExplainContext(): Promise<void> {
        const surface = activeSurface?.readingTarget;

        if (!surface?.isConnected || !canExplainContext) return;

        setTutorStatus("explaining");
        setPreviewState("reading");

        try {
            const explanation = await tutorEngine.explain({
                language,
                context: tutorContext,
                source: extractTutorSource(
                    surface,
                    activeSurface?.contextLabel ?? contextLabel ?? ""
                )
            });

            if (!surface.isConnected) return;

            setTutorResponse({ target: surface, response: explanation });
            setTutorStatus("idle");
            setPreviewState("idle");
            setOpen(false);
            releaseActiveSurface();
        } catch {
            setTutorStatus("explanation-error");
            setPreviewState("oops");
        }
    }

    async function handleGiveHint(): Promise<void> {
        const surface = activeSurface?.readingTarget;
        const exercise = tutorContext.exercise;

        if (
            !surface?.isConnected
            || !canGiveHint
            || !exercise?.type
            || !exercise.objective
            || !exercise.prompt
        ) return;

        setTutorStatus("hinting");
        setPreviewState("hint");

        try {
            const hint = await hintEngine.hint({
                language,
                context: tutorContext,
                exercise: {
                    id: exercise.id,
                    type: exercise.type,
                    objective: exercise.objective,
                    prompt: exercise.prompt
                }
            });

            if (!surface.isConnected) return;

            setTutorResponse({ target: surface, response: hint });
            setTutorStatus("idle");
            setPreviewState("idle");
            setOpen(false);
            releaseActiveSurface();
        } catch {
            setTutorStatus("hint-error");
            setPreviewState("oops");
        }
    }

    function handleStop(): void {
        speech.stop();
        setPreviewState("idle");
    }

    function handleWordMode(): void {
        speech.stop();
        setSelectedText(null);
        selectedRangeRef.current = null;
        setPreviewState("reading");
        setOpen(false);
        setWordMode(true);
    }

    function handleReadSelection(): void {
        const range = selectedRangeRef.current;
        if (!range || !selectedText) return;

        void speech.speakSelection(selectedText, range);
    }

    function exitWordMode(): void {
        speech.stop();
        document.getSelection()?.removeAllRanges();
        selectedRangeRef.current = null;
        setSelectedText(null);
        setWordMode(false);
        setPreviewState("idle");
        setOpen(true);
    }

    function handlePauseResume(): void {
        if (speech.status === "paused") {
            speech.resume();
            return;
        }

        speech.pause();
    }

    const launcher = showLauncher
        ? (
            <button
                type="button"
                className="dino-assistant-launcher"
                aria-label={copy.open}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls="dino-assistant-panel"
                onClick={() => show()}
            >
                <img
                    className="dino-assistant-launcher__image"
                    src={dinoAssistantStateAssets.idle}
                    alt=""
                    aria-hidden="true"
                />
                <span className="dino-assistant-launcher__label">Dino</span>
            </button>
        )
        : null;

    const panel = open
        ? (
            <div className="dino-assistant-layer" data-testid="dino-assistant-layer">
                {isMobileSheet
                    ? (
                        <button
                            type="button"
                            className="dino-assistant-backdrop"
                            aria-label={copy.close}
                            onClick={close}
                        />
                    )
                    : null}
                <section
                    id="dino-assistant-panel"
                    className="dino-assistant-panel"
                    role="dialog"
                    aria-modal={isMobileSheet}
                    aria-labelledby="dino-assistant-title"
                    dir={language === "fa" ? "rtl" : "ltr"}
                >
                    <header className="dino-assistant-panel__header">
                        <div className="dino-assistant-panel__identity">
                            <img
                                className="dino-assistant-panel__mascot"
                                key={visualState}
                                src={dinoAssistantStateAssets[visualState]}
                                alt=""
                                aria-hidden="true"
                            />
                            <div>
                                <span className="dino-assistant-panel__eyebrow">Dino5monde</span>
                                <h2 id="dino-assistant-title">{copy.name}</h2>
                            </div>
                        </div>
                        <button
                            ref={closeButtonRef}
                            type="button"
                            className="dino-assistant-close"
                            aria-label={copy.close}
                            onClick={close}
                        >
                            ×
                        </button>
                    </header>

                    {activeSurface?.contextLabel ?? contextLabel
                        ? (
                            <p className="dino-assistant-context" data-testid="dino-assistant-context">
                                <img
                                    src={dinoAssistantUiIconAssets.context}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <span>{activeSurface?.contextLabel ?? contextLabel}</span>
                            </p>
                        )
                        : null}

                    {speech.currentWord
                        ? (
                            <p
                                className="dino-assistant-reading-word"
                                data-testid="dino-current-word"
                                aria-live="off"
                            >
                                <img
                                    src={dinoAssistantUiIconAssets.speaker}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <span>{copy.readingNow}</span>
                                <strong dir="auto">{speech.currentWord}</strong>
                            </p>
                        )
                        : null}

                    <section className="dino-assistant-section" aria-labelledby="dino-assistant-available-title">
                        <h3 id="dino-assistant-available-title">{copy.available}</h3>
                        <div className="dino-assistant-actions dino-assistant-actions--available">
                            {canGiveHint
                                ? (
                                    <AssistantAction
                                        iconAsset={dinoAssistantUiIconAssets.hint}
                                        label={
                                            tutorStatus === "hinting"
                                                ? copy.hinting
                                                : copy.hintContext
                                        }
                                        description={copy.hintContextDescription}
                                        disabled={tutorBusy}
                                        onClick={() => void handleGiveHint()}
                                    />
                                )
                                : null}
                            {canExplainContext
                                ? (
                                    <AssistantAction
                                        iconAsset={dinoAssistantUiIconAssets.explain}
                                        label={
                                            tutorStatus === "explaining"
                                                ? copy.explaining
                                                : copy.explainContext
                                        }
                                        description={copy.explainContextDescription}
                                        disabled={tutorBusy}
                                        onClick={() => void handleExplainContext()}
                                    />
                                )
                                : null}
                            {activeSurface
                                ? (
                                    <AssistantAction
                                        iconAsset={dinoAssistantUiIconAssets.read}
                                        label={copy.readContext}
                                        description={copy.readContextDescription}
                                        disabled={!speech.supported}
                                        onClick={() => void handleReadContext()}
                                    />
                                )
                                : null}
                            <AssistantAction
                                iconAsset={dinoAssistantUiIconAssets.word}
                                label={copy.readWord}
                                description={copy.readWordDescription}
                                disabled={!speech.supported}
                                onClick={handleWordMode}
                            />
                            {speech.status === "speaking" || speech.status === "paused"
                                ? (
                                    <>
                                        <AssistantAction
                                            iconAsset={dinoAssistantUiIconAssets.stop}
                                            label={copy.stop}
                                            description={copy.stopDescription}
                                            disabled={false}
                                            onClick={handleStop}
                                        />
                                        <AssistantAction
                                            iconAsset={
                                                speech.status === "paused"
                                                    ? dinoAssistantUiIconAssets.play
                                                    : dinoAssistantUiIconAssets.pause
                                            }
                                            label={speech.status === "paused" ? copy.resume : copy.pause}
                                            description={speech.status === "paused" ? copy.resumeDescription : copy.pauseDescription}
                                            disabled={false}
                                            onClick={handlePauseResume}
                                        />
                                    </>
                                )
                                : null}
                        </div>
                        <details className="dino-assistant-settings">
                            <summary>{copy.settingsLabel}</summary>
                            <label>
                                <span>{copy.speed}: {speech.settings.rate.toFixed(1)}×</span>
                                <input
                                    type="range"
                                    min="0.7"
                                    max="1.3"
                                    step="0.1"
                                    value={speech.settings.rate}
                                    onChange={event => speech.updateSettings({
                                        rate: Number(event.target.value)
                                    })}
                                />
                            </label>
                            {relevantVoices.length > 0
                                ? (
                                    <label>
                                        <span>{copy.voice}</span>
                                        <select
                                            value={selectedVoiceId}
                                            onChange={event => speech.updateSettings({
                                                voiceId: event.target.value || null
                                            })}
                                        >
                                            <option value="">{copy.automaticVoice}</option>
                                            {relevantVoices.map(voice => (
                                                <option key={voice.id} value={voice.id}>
                                                    {voice.name}
                                                    {voice.quality === "natural"
                                                        ? " · " + copy.naturalVoice
                                                        : ""}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )
                                : null}
                        </details>
                        {!speech.supported
                            ? <p className="dino-assistant-notice" role="status">{copy.unsupported}</p>
                            : null}
                        {tutorStatus === "explanation-error"
                            ? <p className="dino-assistant-notice" role="alert">{copy.explanationError}</p>
                            : null}
                        {tutorStatus === "hint-error"
                            ? <p className="dino-assistant-notice" role="alert">{copy.hintError}</p>
                            : null}
                    </section>

                    <details className="dino-assistant-section dino-assistant-future">
                        <summary>
                            <span>{copy.futureTitle}</span>
                            <span className="dino-assistant-future__badge">{copy.comingSoon}</span>
                        </summary>
                        <p className="dino-assistant-section__intro">{copy.futureDescription}</p>
                        <div className="dino-assistant-actions">
                            {recommendedActions.map((action) => (
                                <AssistantAction
                                    key={action.id}
                                    iconAsset={action.iconAsset}
                                    label={copy.actions[action.id]}
                                    description={copy.descriptions[action.id]}
                                    disabled
                                />
                            ))}
                        </div>
                        <p className="dino-assistant-privacy">
                            <img
                                src={dinoAssistantUiIconAssets.privacy}
                                alt=""
                                aria-hidden="true"
                            />
                            <span>{copy.privacy}</span>
                        </p>
                    </details>
                </section>
            </div>
        )
        : null;

    const wordToolbar = wordMode
        ? (
            <aside
                className="dino-word-reader-toolbar"
                aria-label={copy.readWord}
                dir={language === "fa" ? "rtl" : "ltr"}
            >
                <img
                    src={dinoAssistantStateAssets.reading}
                    alt=""
                    aria-hidden="true"
                />
                <div>
                    <strong>{copy.readWord}</strong>
                    <span>{selectedText ?? speech.currentWord ?? copy.pickWordHint}</span>
                </div>
                <div className="dino-word-reader-toolbar__actions">
                    {selectedText
                        ? (
                            <button
                                type="button"
                                onMouseDown={event => event.preventDefault()}
                                onClick={handleReadSelection}
                            >
                                {copy.readSelection}
                            </button>
                        )
                        : null}
                    <button type="button" onClick={exitWordMode}>
                        {copy.exitWordMode}
                    </button>
                </div>
            </aside>
        )
        : null;

    const renderedPanel =
        panel && !renderInline && typeof document !== "undefined"
            ? createPortal(panel, document.body)
            : panel;

    const renderedWordToolbar =
        wordToolbar && typeof document !== "undefined"
            ? createPortal(wordToolbar, document.body)
            : wordToolbar;

    const renderedTutorResponse =
        tutorResponse?.target.isConnected && typeof document !== "undefined"
            ? createPortal(
                <TutorResponseCard
                    response={tutorResponse.response}
                    language={language}
                    sourceKind={
                        tutorResponse.response.mode === "guarded-hint"
                            ? copy.guidedHintSource
                            : copy.canonicalSource
                    }
                    closeLabel={
                        tutorResponse.response.mode === "guarded-hint"
                            ? copy.closeHint
                            : copy.closeExplanation
                    }
                    onClose={() => setTutorResponse(null)}
                />,
                tutorResponse.target
            )
            : null;

    return <>{launcher}{renderedPanel}{renderedWordToolbar}{renderedTutorResponse}</>;
}

interface TutorResponseCardProps {
    response: TutorResponse;
    language: "fr" | "fa";
    sourceKind: string;
    closeLabel: string;
    onClose: () => void;
}

function TutorResponseCard({
    response,
    language,
    sourceKind,
    closeLabel,
    onClose
}: TutorResponseCardProps) {
    return (
        <aside
            className="dino-tutor-answer"
            data-testid="dino-tutor-answer"
            aria-live="polite"
            data-tutor-mode={response.mode}
            dir={language === "fa" ? "rtl" : "ltr"}
        >
            <header className="dino-tutor-answer__header">
                <span className="dino-tutor-answer__source">
                    <img
                        src={dinoAssistantUiIconAssets.context}
                        alt=""
                        aria-hidden="true"
                    />
                    {sourceKind}
                </span>
                <button
                    type="button"
                    className="dino-tutor-answer__close"
                    aria-label={closeLabel}
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onClose();
                    }}
                >
                    ×
                </button>
            </header>
            <h3>{response.title}</h3>
            <p>{response.summary}</p>
            {response.points.length > 0
                ? (
                    <ul>
                        {response.points.map(point => (
                            <li key={point}>{point}</li>
                        ))}
                    </ul>
                )
                : null}
            <p className="dino-tutor-answer__provenance">
                {response.sourceLabel}
            </p>
        </aside>
    );
}

interface AssistantActionProps {
    iconAsset: string;
    label: string;
    description: string;
    disabled: boolean;
    badge?: string;
    onClick?: () => void;
}

function AssistantAction({
    iconAsset,
    label,
    description,
    disabled,
    badge,
    onClick
}: AssistantActionProps) {
    return (
        <button
            type="button"
            className="dino-assistant-action"
            disabled={disabled}
            aria-disabled={disabled}
            onClick={onClick}
        >
            <span className="dino-assistant-action__icon" aria-hidden="true">
                <img src={iconAsset} alt="" />
            </span>
            <span className="dino-assistant-action__copy">
                <span className="dino-assistant-action__title">
                    {label}
                    {badge ? <span className="dino-assistant-action__badge">{badge}</span> : null}
                </span>
                <span className="dino-assistant-action__description">{description}</span>
            </span>
        </button>
    );
}

export { DinoAssistant };
export type { DinoAssistantProps };
